import * as React from "react"
import compose from "recompose/compose"
import { dateLong, daysAgo, today } from "@/helpers/dates"
import { DateUtils } from "react-day-picker"
import { withi18n } from "@/helpers/hoc/withi18n"
import { withDateRange } from "@/helpers/hoc/withDateRange"
import { withFeatureFlags } from "@/helpers/hoc/withFeatureFlags"
import { DateRangePickerView } from "./components"
import withBenchmarkSettingsContext from "@/helpers/hoc/withBenchmarkSettingsContext"

const i18n = {
  lastNumberOfDays: (p) => "360_RECRUITER_INSIGHTS_OVERVIEW_LAST_DAYS",
}

class DateRangePicker extends React.Component {
  static defaultProps = {
    numberOfMonths: 2,
    disabledDays: { after: new Date() },
  }

  state = {
    dateRange: {
      from: this.props.from,
      to: this.props.to,
      triggerText: isNaN(this.props.last)
        ? `${dateLong(this.props.from)} - ${dateLong(this.props.to)}`
        : this.props.lastNumberOfDays({ days: this.props.last }),
      lastDaysValue: `${this.props.last}` || "30",
      highlight: null,
    },
  }

  componentDidUpdate(prevProps, prevState) {
    const { dateRange } = this.state

    if (
      dateRange !== prevState.dateRange &&
      dateRange.to !== null &&
      dateRange.to !== undefined
    ) {
      this.props.setDateRange(dateRange)
    }
  }

  onDateRangeChange = (value) => {
    const {
      props: { setLast, lastNumberOfDays },
      state: {
        dateRange: { from, to },
      },
    } = this

    setLast(value)

    if (value === "custom") {
      return this.setState((state) => ({
        dateRange: {
          ...state.dateRange,
          triggerText: `${dateLong(from)} - ${dateLong(to)}`,
          lastDaysValue: "custom",
        },
      }))
    }

    this.setState((state) => ({
      dateRange: {
        ...state.dateRange,
        from: daysAgo(value),
        to: today(),
        triggerText: lastNumberOfDays({ days: value }),
        lastDaysValue: value,
      },
    }))
  }

  onInputDateFocus = (type) => {
    this.setState((state) => ({
      dateRange: {
        ...state.dateRange,
        highlight: type,
      },
    }))
  }

  setDateRangeStatus = (range) => {
    this.props.setLast("custom")

    return range.from.toString() === range.to.toString()
      ? dateLong(range.from)
      : `
        ${dateLong(range.from)} - ${dateLong(range.to)}
      `
  }

  handleDayClick = (day, modifiers = {}) => {
    const {
      dateRange: { from, highlight, to },
    } = this.state

    let range, triggerText

    if (modifiers.disabled) return

    if (highlight === "from") {
      if (to !== null) {
        range = DateUtils.isDayAfter(day, to)
          ? DateUtils.addDayToRange(day, { from: to, to: null })
          : DateUtils.addDayToRange(day, { from: null, to })

        triggerText = this.setDateRangeStatus(range)
      } else {
        range = DateUtils.addDayToRange(day, { from: null, to: null })
        triggerText = this.state.dateRange.triggerText
      }
    } else if (highlight === "to") {
      range = DateUtils.isDayBefore(day, from)
        ? DateUtils.addDayToRange(day, { from: null, to: from })
        : DateUtils.addDayToRange(day, { from, to: null })
      triggerText = this.setDateRangeStatus(range)
    } else if (from !== null && to !== null) {
      range = DateUtils.addDayToRange(day, { from: null, to: null })
      triggerText = this.state.dateRange.triggerText
    } else {
      range = DateUtils.addDayToRange(day, this.state.dateRange)
      triggerText = this.setDateRangeStatus(range)
    }

    this.setState((state) => ({
      dateRange: {
        ...state.dateRange,
        triggerText,
        lastDaysValue: "custom",
        highlight: null,
        ...range,
      },
    }))
  }

  render() {
    const {
      props: {
        disabledDays,
        numberOfMonths,
        enabledFeatures,
        contextProps: { benchmarkSettings },
      },
      state: { dateRange },
    } = this
    const modifiers = {
      start: dateRange.from,
      end: dateRange.to,
      highlight: dateRange.highlight && dateRange[dateRange.highlight],
    }

    return (
      <DateRangePickerView
        dateRange={dateRange}
        disabledDays={disabledDays}
        modifiers={modifiers}
        numberOfMonths={numberOfMonths}
        onDayClick={this.handleDayClick}
        onDateRangeChange={this.onDateRangeChange}
        onInputDateFocus={this.onInputDateFocus}
        enabledFeatures={enabledFeatures}
        benchmarkSettings={benchmarkSettings}
      />
    )
  }
}

const enhance = compose(
  withi18n(i18n),
  withDateRange,
  withFeatureFlags,
  withBenchmarkSettingsContext,
)

export default enhance(DateRangePicker)
